import React, { useEffect, useState } from 'react';
import { getHelixGlobal } from '../../core/HelixGlobals';
import { Button } from '../primitives/Button';
import { PLAYER_UI, playerPanel } from '../theme';

export interface BootState {
  initCalled: boolean; gameCreated: boolean; scenesAdded: boolean; readyFired: boolean;
  sceneStarted: boolean; progress: number; stage: string; error: string | null; timestamp: number;
}

export function loadingStageLabel(stage: string): string {
  const value = stage.toLowerCase();
  if (value.includes('waiting for server')) return 'Connecting to the world…';
  if (value.includes('entering world')) return 'Opening your journey…';
  if (value.includes('ability')) return 'Preparing your calling…';
  if (value.includes('loading assets')) return 'Gathering the world’s artwork…';
  return 'Preparing the world…';
}

/** Every field the loading screen reads, as one comparable string. */
export function bootStateKey(state: BootState): string {
  return [state.initCalled, state.gameCreated, state.scenesAdded, state.readyFired, state.sceneStarted,
    state.progress, state.stage, state.error, state.timestamp].join('|');
}

/** An explicit ready flag comes from Game's actual created world, not preload start. */
export function usePhaserLoading(worldReady?: boolean) {
  const [bootState, setBootState] = useState<BootState | null>(null);
  const [complete, setComplete] = useState(false);
  useEffect(() => {
    let completeTimer: ReturnType<typeof setTimeout> | undefined;
    let lastBoot: number | undefined;
    // The boot record is mutated in place, so compare its fields: the poll
    // keeps running (a later error or re-boot must show), but it only commits
    // when something changed (B7: it re-rendered at 10 Hz all game long).
    let lastKey: string | null = null;
    const update = () => {
      const state = getHelixGlobal('__GAME_BOOT__');
      const key = state ? bootStateKey(state) : null;
      if (key !== lastKey) {
        lastKey = key;
        setBootState(state ? { ...state } : null);
      }
      if (!state) return;
      if (lastBoot !== state.timestamp) {
        lastBoot = state.timestamp;
        setComplete(false);
        if (completeTimer) clearTimeout(completeTimer);
        completeTimer = undefined;
      }
      const ready = worldReady ?? state.sceneStarted;
      if (state.error || !ready || state.progress < 1) {
        if (completeTimer) clearTimeout(completeTimer);
        completeTimer = undefined;
        setComplete(false);
      } else if (!completeTimer) {
        completeTimer = setTimeout(() => setComplete(true), 500);
      }
    };
    update();
    const interval = setInterval(update, 100);
    return () => { clearInterval(interval); if (completeTimer) clearTimeout(completeTimer); };
  }, [worldReady]);
  return { bootState, complete };
}

export const LoadingScreen: React.FC<{ worldReady?: boolean; onRetry?: () => void }> = ({ worldReady, onRetry }) => {
  const { bootState, complete } = usePhaserLoading(worldReady);
  if (!bootState || (complete && !bootState.error)) return null;
  const progress = Number.isFinite(bootState.progress) ? Math.round(Math.min(1, Math.max(0, bootState.progress)) * 100) : 0;
  return <div style={{ position: 'absolute', inset: 0, zIndex: 9999, display: 'grid', placeItems: 'center', overflow: 'auto', padding: 24, boxSizing: 'border-box', background: `radial-gradient(ellipse at 50% 30%,rgba(201,163,92,.1),transparent 60%),${PLAYER_UI.inkDeep}`, fontFamily: PLAYER_UI.bodyFont }}>
    <section aria-label="Loading Helix 2000" style={{ ...playerPanel(), padding: 'clamp(24px,5vw,44px)', width: 'min(100%,460px)', boxSizing: 'border-box', textAlign: 'center' }}>
      <img src="/icons/abilities/word_of_shielding.svg" alt="" width={64} height={64} />
      <h1 style={{ fontFamily: PLAYER_UI.displayFont, fontWeight: 500, fontSize: 28, margin: '20px 0 12px' }}>Helix 2000</h1>
      {bootState.error ? <div role="alert">
        <h2 style={{ fontSize: 17, margin: '16px 0 8px', color: PLAYER_UI.text }}>The world could not be opened</h2>
        <p style={{ color: PLAYER_UI.secondary, fontSize: 14, lineHeight: 1.6 }}>Please try again to continue your journey.</p>
        <Button variant="brass" size="lg" onClick={() => { if (onRetry) onRetry(); else window.location.reload(); }}>Try again</Button>
      </div> : <>
        <p role="status" style={{ color: PLAYER_UI.secondary, fontSize: 14, margin: '0 0 22px' }}>{loadingStageLabel(bootState.stage)}</p>
        <div role="progressbar" aria-label="World assets" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}
          style={{ height: 5, borderRadius: 4, overflow: 'hidden', background: PLAYER_UI.well, border: `1px solid ${PLAYER_UI.softBorder}` }}>
          <div style={{ width: `${progress}%`, height: '100%', background: PLAYER_UI.brassBright }} />
        </div>
        <p style={{ fontSize: 12, color: PLAYER_UI.muted, margin: '10px 0 0' }}>{progress}%</p>
      </>}
    </section>
  </div>;
};
